import {
    getWithAuth,
    postWithAuth,
    putWithAuth,
    deleteWithAuth
} from '@/api/base/authorised-requests'
import {
    VerifyPersonRequest,
    VerifyPersonResponse,
    Dictionary,
    BankAccountVerificationRequest,
    BankAccountVerificationResponse,
    ApplicationValidationResponse,
    OnboardingApplication,
    OnboardingProcessStatusResponse
} from '@/stores/onboarding/models/onboarding'
import {SaveApplicationRequest} from '@/stores/onboarding/models/onboarding'
import {ApiResponse} from 'back-connector'
import {storage} from '@/services/storage'
import {ACCESS_TOKEN_KEY, VISITOR_ID_KEY, X_CLIENT_ID_KEY} from '@/constants/auth-constants'
import {onboardingMessage, cleanErrorMessage} from '@/pages/onboarding/utils'

export const ONBOARDING_BASE_URL = '/self-onboarding-api'

export const fetchSelfOnboardingProcessStatus = async (
    processKey: string
): Promise<ApiResponse<OnboardingProcessStatusResponse>> => {
    try {
        const response = await getWithAuth<OnboardingProcessStatusResponse>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}`
        )
        return response
    } catch (error) {
        console.error('Error fetching process status:', error)
        throw error
    }
}

export const startSelfOnboardingApplication = async (
    processKey: string,
    data: any
): Promise<any> => {
    try {
        const response = await postWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}`,
            data
        )

        if (!response || response.error) {
            throw response.error
        }

        return response.result
    } catch (error) {
        throw error
    }
}

export const fetchSelfOnboardingApplicationDetails = async (
    processKey: string
): Promise<OnboardingApplication> => {
    try {
        const response = await getWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}/application`
        )

        const result = response?.result

        if (result) {
            if (
                typeof result === 'object' &&
                'activeProcess' in result &&
                result.activeProcess &&
                typeof result.activeProcess === 'object' &&
                'application' in result.activeProcess
            ) {
                return result.activeProcess.application
            }

            return result
        }

        return response.result
    } catch (error) {
        console.error('Error fetching application details:', error)
        throw error
    }
}

export const saveSelfOnboardingApplication = async (
    processKey: string,
    data: SaveApplicationRequest
): Promise<any> => {
    const response = await putWithAuth<any>(
        `${ONBOARDING_BASE_URL}/process/key/${processKey}/application`,
        data
    )
    return response.result
}

export const submitSelfOnboardingApplication = async (
    processKey: string,
    data: SaveApplicationRequest
): Promise<any> => {
    const response = await postWithAuth<any>(
        `${ONBOARDING_BASE_URL}/process/key/${processKey}/application`,
        data
    )

    return response.result
}

export const fetchSelfOnboardingCompanyByName = async (name: string): Promise<any> => {
    try {
        const response = await getWithAuth<any>(
            `${ONBOARDING_BASE_URL}/company?name=${encodeURIComponent(name)}`
        )
        return response.result
    } catch (error) {
        console.error('Error fetching company by name:', error)
        throw error
    }
}

export const fetchSelfOnboardingAddressByPostalCode = async (postcode: string): Promise<any> => {
    const response = await getWithAuth<any>(
        `${ONBOARDING_BASE_URL}/address?postcode=${encodeURIComponent(postcode)}`
    )
    return response.result
}

export const fetchSelfOnboardingDictionary = async (category: string): Promise<Dictionary> => {
    const response = await getWithAuth<Dictionary>(
        `${ONBOARDING_BASE_URL}/dictionary/category/${category}`
    )
    return response.result
}

export const verifySelfOnboardingPerson = async (
    data: VerifyPersonRequest
): Promise<VerifyPersonResponse> => {
    const response = await postWithAuth<VerifyPersonResponse>(
        `${ONBOARDING_BASE_URL}/person/verify`,
        data
    )
    return response.result
}

export const verifySelfOnboardingBankAccountPersonal = async (
    data: BankAccountVerificationRequest
): Promise<BankAccountVerificationResponse> => {
    const response = await postWithAuth<BankAccountVerificationResponse>(
        `${ONBOARDING_BASE_URL}/bank-account/verify`,
        data
    )

    if (response.error) {
        throw response.error
    }

    return response.result
}

export const verifySelfOnboardingBankAccountBusiness = async (
    data: Omit<BankAccountVerificationRequest, 'businessOwner'>
): Promise<BankAccountVerificationResponse> => {
    const response = await postWithAuth<BankAccountVerificationResponse>(
        `${ONBOARDING_BASE_URL}/bank-account/verify`,
        data
    )

    if (response.error) {
        throw response.error
    }

    return response.result
}

export const validateSelfOnboardingApplication = async (
    data: any
): Promise<ApplicationValidationResponse> => {
    try {
        const response = await postWithAuth<ApplicationValidationResponse>(
            `${ONBOARDING_BASE_URL}/process/application`,
            data
        )
        return response.result
    } catch (error) {
        console.error('Error validating application:', error)
        throw error
    }
}

export const submitApplication = async (processKey: string): Promise<any> => {
    try {
        const response = await postWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}/submit`,
            {}
        )
        return response.result
    } catch (error) {
        console.error('Error submitting application:', error)
        throw error
    }
}

export const terminateSelfOnboardingProcess = async (processKey: string): Promise<any> => {
    try {
        const response = await deleteWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}`
        )
        return response.result
    } catch (error) {
        console.error('Error terminating process:', error)
        throw error
    }
}

export const submitAdditionalInfo = async (
    processKey: string,
    data: {
        revertReasons: Array<{
            id: string
            name: string
            key: string
            date: string
            category: string
            reason: string
            comment: string
            responsibleTeam: string
            answer: string
            answerDate: string
            files: any
        }>
    }
): Promise<any> => {
    try {
        const response = await postWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/key/${processKey}/application/additional-info`,
            data
        )

        if (response.error) {
            onboardingMessage.error(cleanErrorMessage(response?.error?.message))
            throw response.error
        }

        return response.result
    } catch (error) {
        throw error
    }
}

export const fetchMsaPreview = async (data: {name: string; email: string}): Promise<Blob> => {
    const response = await postWithAuth<Blob>(
        `${ONBOARDING_BASE_URL}/process/contract-signing/msa/preview`,
        data,
        [],
        {
            parseResponse: async (res: Response) => {
                if (res.status !== 200) {
                    const errorText = await res.text()
                    throw new Error(errorText || 'Failed to fetch MSA preview')
                }
                return await res.blob()
            }
        }
    )

    if (response.error) {
        throw response.error
    }

    return response.result
}

export const submitReviewAndSign = async (processKey: string): Promise<void> => {
    const response = await postWithAuth<void>(
        `${ONBOARDING_BASE_URL}/process/key/${processKey}/application/review-and-sign-info`,
        {}
    )

    if (response.error) {
        onboardingMessage.error(cleanErrorMessage(response?.error?.message))
        throw response.error
    }
}

export const resendContractSigningEmail = async (processKey: string): Promise<any> => {
    try {
        const response = await postWithAuth<any>(
            `${ONBOARDING_BASE_URL}/process/contract-signing/email/resend`,
            {processKey}
        )
        return response.result
    } catch (error) {
        console.error('Error resending contract signing email:', error)
        throw error
    }
}

export const uploadOnboardingFiles = async (
    processKey: string,
    files: File[],
    documentType?: string
): Promise<{success: boolean; uploadedFiles?: any[]; errors?: string[]}> => {
    try {
        const formData = new FormData()
        files.forEach((file) => {
            formData.append('files', file)
        })

        const accessToken = storage.get(ACCESS_TOKEN_KEY)
        const visitorId = storage.get(VISITOR_ID_KEY)

        const response = await fetch(`${ONBOARDING_BASE_URL}/process/key/${processKey}/files`, {
            method: 'POST',
            body: formData,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                ...(visitorId && {[X_CLIENT_ID_KEY]: visitorId})
            }
        })

        if (!response.ok) {
            const errorText = await response.text()
            return {
                success: false,
                errors: [`Upload failed (${response.status}): ${errorText}`]
            }
        }

        const result = await response.json()

        if (result?.result) {
            return {
                success: true,
                uploadedFiles: result.result.uploadedFiles || result.result || []
            }
        } else if (result?.uploadedFiles) {
            return {
                success: true,
                uploadedFiles: result.uploadedFiles
            }
        } else if (Array.isArray(result)) {
            return {
                success: true,
                uploadedFiles: result
            }
        } else {
            return {
                success: true,
                uploadedFiles: []
            }
        }
    } catch (error: any) {
        return {
            success: false,
            errors: [error.message || 'File upload failed']
        }
    }
}
